<template>
  <main>
    <Trail
      :list="[
        {
          path: '/proyectos/investigacion',
          name: 'Projects',
          tag: 'Proyectos de Investigación'
        },
        {
          name: 'MAREMEX',
          tag: 'MAREMEX'
        }
      ]"
    />
    <Container class="container--main">
      <Title tag="h1" align="center">
        Manejo de desastres ante eventos meteorológicos extremos (sequías,
        heladas y lluvias intensas) como medida de adaptación ante el cambio
        climático en el valle del Mantaro (MAREMEX-Mantaro)
      </Title>
      <Row>
        <ColumnUi md="9">
          <Navtab id="introduccion">
            <Title tag="h2">Introducción</Title>
            <p>
              El Perú es particularmente vulnerable al cambio climático debido a
              su gran diversidad ecológica y a la escasez de recursos económicos
              y tecnológicos con que cuenta su población. Esto se pone en
              evidencia todos los años, en los que eventos meteorológicos
              extremos como huaycos, heladas, sequías, etc. afectan recurrente y
              negativamente a la población y a sus actividades, sin que se tomen
              mayores medidas preventivas. Más aún, uno de los posibles impactos
              negativos del cambio climático podría ser un aumento en la
              frecuencia o magnitud de dichos eventos extremos. Por lo anterior,
              la idea fundamental detrás del presente proyecto es que la
              implementación de un sistema de gestión de riesgos ante fenómenos
              que nos afectan en la actualidad es un necesario primer paso para
              la adaptación a cambios en el clima.
            </p>
          </Navtab>
          <Navtab id="antecedentes">
            <Title tag="h2">
              Antecedentes
            </Title>
            <p>
              A inicios del año 2007, el
              <a href="http://www.idrc.ca"
                >International Development Research Centre (IDRC)</a
              >
              de Canadá mostró interés en que el Instituto Geofísico del Perú
              presentara una nota conceptual relacionada con los temas de
              vulnerabilidad y adaptación al cambio climático, con vistas a
              desarrollar un perfil que pudiera ser incluido dentro de los
              estudios priorizados del IDRC.
            </p>
            <p>
              El interés del IDRC en que el IGP presentara una propuesta se basó
              en la experiencia y el trabajo desarrollado por el IGP en la
              cuenca del río Mantaro:
              <a href="../proclim"
                >"Estudio de Vulnerabilidad y Adaptación al cambio climático en
                la cuenca del río Mantaro"</a
              >
              (2003-2005); y del
              <a
                href="http://www.met.igp.gob.pe/cpntc/users/ale_mar/martinez_2007.pdf"
                >"Análisis de la vulnerabilidad ante los efectos del cambio
                climático y propuestas de adaptación en la ciudad de Huancayo,
                Junín: Recurso agua y género"</a
              >
              (2007). Asimismo por estar desarrollando el proyecto
              <a href="../incagro"
                >"Pronóstico estacional de lluvias y temperaturas en la cuenca
                del río Mantaro para su aplicación en la agricultura"</a
              >
              (2007-2009), con financiamiento del programa
              <a href="http://www.incagro.gob.pe">INCAGRO</a>.
            </p>
            <p>
              El IGP empezó las coordinaciones necesarias para que la nota
              conceptual se convirtiera en un perfil que cumpliera con las
              especificaciones solicitadas por el IDRC, específicamente por el
              <a href="http://www.idrc.ca/en/ev-58566-201-1-DO_TOPIC.html"
                >Rural Poverty and Environment Program</a
              >
              de dicha institución. En el transcurso del año 2008 se continúo
              con el desarrollo del perfil del proyecto, y ese mismo año el IDRC
              verificó que el IGP cumpliera con las formalidades administrativas
              necesarias para manejar fondos otorgados por el IDRC.
            </p>
            <p>
              En febrero de 2009, se firmaron los documentos oficiales del
              convenio entre el IGP y el IDRC, y se dio inicio al proyecto de
              investigación: "Manejo de riesgo de desastres ante eventos
              meteorológicos extremos (sequías, heladas y lluvias intensas) como
              medida de adaptación ante el cambio climático en el valle del
              Mantaro" (MAREMEX-Mantaro), que tendrá una duración de 3 años
            </p>
          </Navtab>
          <Navtab id="zona-estudio">
            <Title tag="h2">
              Zona de estudio
            </Title>
            <p>
              El valle del Mantaro está localizado en los Andes Centrales
              peruanos, y es una zona particularmente vulnerable a la ocurrencia
              de sequías, heladas y lluvias intensas, eventos que tienen
              impactos muy serios para la población y sus principales sectores
              económicos, los mismos que se ven agravados porque es frecuente
              que estos eventos ocurran simultáneamente.
            </p>
            <p>
              La agricultura de la zona provee con importantes productos (papa,
              haba, maíz, kiwicha, etc.) a las principales ciudades de la costa,
              como la capital del país, Lima. Por otro lado, entre el 70-75% de
              la agricultura se hace bajo el sistema de secano, es decir,
              dependiente de las lluvias.
            </p>
            <p>
              Además, la capacidad de manejo y gestión de riesgos es muy escasa
              en la zona, por lo que el principal objetivo del proyecto es el
              fortalecer la capacidad de manejo del riesgo a eventos
              meteorológicos extremos (sequías, heladas y lluvias intensas), a
              fin de disminuir la vulnerabilidad y mejorar la capacidad de
              adaptación de la población urbana y rural en el valle del Mantaro
              frente a cambios del clima.
            </p>
            <p>
              Los estudios se concentran en 3 subcuencas: Achamayo, Shullcas y
              Cunas, tal como se muestra en la figura siguiente:
            </p>
            <Figure>
              <Image
                linked
                :srcsets="[
                  '/images/proyectos/maremex/mapa-base-subcuencas.gif'
                ]"
                title="Subcuencas priorizadas en el proyecto"
              />
              <template #figcaption>
                Subcuencas priorizadas en el proyecto
              </template>
            </Figure>
          </Navtab>
          <Navtab id="objetivos">
            <Title tag="h2">
              Objetivo principal y específicos del proyecto
            </Title>
            <p>
              El objetivo principal del proyecto es el de fortalecer la
              capacidad de manejo del riesgo a eventos meteorológicos extremos
              (sequías, heladas y lluvias intensas), a fin de disminuir la
              vulnerabilidad y mejorar la capacidad de adaptación de la
              población urbana y rural en el valle del Mantaro frente a cambios
              del clima.
            </p>
            <p>
              Este objetivo principal se llevará a cabo a través de 4 objetivos
              específicos:
            </p>
            <List tag="ol">
              <li>
                Fortalecer y profundizar los estudios sobre causas, ocurrencia e
                impactos de heladas, sequías y fenómenos de lluvias intensas la
                región.
              </li>
              <li>
                Identificar los actores claves involucrados y evaluar las
                actuales capacidades de los pobladores del valle del Mantaro en
                el manejo del riesgo de desastres, por eventos climáticos
                extremos.
              </li>
              <li>
                Elaborar un plan integrado de manejo de riesgo y estrategias de
                adaptación frente a heladas, sequías y lluvias intensas en el
                valle del Mantaro, con la participación de autoridades locales,
                gobiernos regionales, comunidades, ONG y otros actores
                relevantes.
              </li>
              <li>
                Fortalecer las instituciones locales y regionales y difundir los
                resultados del proyecto a la población, instituciones y medios
                científicos, a través de la creación o fortalecimiento de
                capacidades de investigación de entidades e investigadores
                locales en temas de adaptación al cambio climático.
              </li>
            </List>
          </Navtab>
          <Navtab id="estrategias">
            <Title tag="h2">
              Estrategias del proyecto
            </Title>
            <p>
              Las principales estrategias de trabajo que se utilizarán para
              lograr los objetivos del proyecto, y lograr la adopción efectiva
              de políticas de manejo de desastres en la región son:
            </p>
            <List tag="ol">
              <li>
                Estudio conjunto de los entornos urbano y rural, y que contempla
                los temas de género y recurso agua como ejes transversales de
                trabajo desde el inicio del proyecto.
              </li>
              <li>
                Desarrollo de temas de investigación a través de tesis de
                investigación de pregrado y postgrado, con el fin de crear
                capacidades locales.
              </li>
              <li>
                Creación de espacios de trabajo común entre tomadores de
                decisiones, investigadores y beneficiarios del proyecto.
              </li>
              <li>
                Fortalecimiento de capacidades locales, regionales y nacionales
                para participar en investigaciones interdisciplinarias.
              </li>
              <li>
                Amplia diseminación de los resultados del proyecto a tres
                niveles: Comunidad científica internacional; ámbito
                institucional y usuarios finales.
              </li>
            </List>
          </Navtab>
          <Navtab id="material">
            <Title tag="h2">
              Material de difusión
            </Title>
            <List tag="ol">
              <li>
                <a
                  href="https://repositorio.igp.gob.pe/handle/20.500.12816/740"
                  target="_blank"
                >
                  Libro "Eventos meteorológicos extremos (sequías, heladas y
                  lluvias intensas) en el valle del Mantaro. Volumen I de
                  resultados del proyecto MAREMEX-Mantaro".
                </a>
              </li>
              <li>
                <a
                  href="https://repositorio.igp.gob.pe/handle/20.500.12816/2735"
                  target="_blank"
                >
                  Libro "Manejo de desastres ante eventos meteorológicos
                  extremos en el valle del Mantaro. Volumen II de resultados del
                  proyecto MAREMEX-Mantaro".
                </a>
              </li>
              <li>
                <a
                  href="https://repositorio.igp.gob.pe/handle/20.500.12816/710"
                  target="_blank"
                >
                  Libro "Cambio climático en la cuenca del río Mantaro: Balance
                  de 7 años de estudio en la cuenca del Mantaro
                </a>
              </li>
              <li>
                <a
                  href="https://repositorio.igp.gob.pe/handle/20.500.12816/936"
                  target="_blank"
                >
                  Boletín Nro. 1 del proyecto MAREMEX-Mantaro
                </a>
              </li>
              <li>
                <a
                  href="https://repositorio.igp.gob.pe/handle/20.500.12816/937"
                  target="_blank"
                >
                  Boletín Nro. 2 del proyecto MAREMEX-Mantaro
                </a>
              </li>
              <li>
                <a
                  href="http://met.igp.gob.pe/publicaciones/2012/BoletinMaremex3.pdf"
                  target="_blank"
                >
                  Boletín Nro. 3 del proyecto MAREMEX-Mantaro
                </a>
              </li>
            </List>
          </Navtab>
          <Navtab id="bases-datos">
            <Title tag="h2">
              Bases de datos
            </Title>
            <List tag="ol">
              <li>
                Noticias Mantaro (<a
                  href="http://met.igp.gob.pe/proyectos/maremex/BasedeDatos/Instrucciones.txt"
                  >Instrucciones</a
                >) (<a
                  href="http://met.igp.gob.pe/proyectos/maremex/BasedeDatos/Archivos/"
                  >Archivos</a
                >)
              </li>
            </List>
          </Navtab>
        </ColumnUi>
        <ColumnUi md="3">
          <Nav :items="navItems" title="Contenido" />
        </ColumnUi>
      </Row>
    </Container>
  </main>
</template>
<script>
import Trail from "@/components/common/Trail";
import Title from "@/components/ui/atoms/Title";
import List from "@/components/ui/objects/List";
import Container from "@/components/ui/objects/Container";
import Figure from "@/components/ui/objects/Figure";
import Image from "@/components/ui/objects/Image";
import Nav from "@/components/ui/objects/Nav";
import Navtab from "@/components/ui/objects/Navtab";
import Row from "@/components/ui/atoms/Row";
import ColumnUi from "@/components/ui/atoms/ColumnUi";
export default {
  components: {
    Trail,
    Title,
    List,
    Container,
    Figure,
    Image,
    Nav,
    Navtab,
    Row,
    ColumnUi
  },
  setup() {
    const navItems = [
      {
        path: "#introduccion",
        tag: "Introducción"
      },
      {
        path: "#antecedentes",
        tag: "Antecedentes"
      },
      {
        path: "#zona-estudio",
        tag: "Zona de estudio"
      },
      {
        path: "#objetivos",
        tag: "Objetivo principal y específicos del proyecto"
      },
      {
        path: "#estrategias",
        tag: "Estrategias del proyecto"
      },
      {
        path: "#material",
        tag: "Material de difusión"
      },
      {
        path: "#bases-datos",
        tag: "Bases de datos"
      }
    ];
    return {
      navItems
    };
  }
};
</script>
